import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { EmptyState } from '@/presentation/components/common/EmptyState';
import type {
  CampaignPatient,
  Campaign,
  CampaignStatus,
  RecipientPreset,
} from '@/application/campaigns/campaignsTypes';
import {
  createCampaign,
  getCampaign,
  listCampaigns,
  previewAudience,
  searchPatients,
  sendCampaign,
  updateCampaign,
} from '@/infrastructure/http/campaignsApi';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';

// ── Tipos de UI ──────────────────────────────────────────────────────────────

type EmailDraft = {
  replyTo: string;
  subject: string;
  preheader: string;
  body: string;
};

/** Estado simplificado del composer (el backend maneja el resto de estados). */
type ComposerStatus = 'draft' | 'ready' | 'sent';
type DeliveryMode = 'send_now' | 'schedule';
type AiSuggestion = EmailDraft;

type Recipient = { id: number; name: string; email: string };

const EMPTY_DRAFT: EmailDraft = { replyTo: '', subject: '', preheader: '', body: '' };

const SEGMENT_PRESETS: RecipientPreset[] = [
  'all_active',
  'debtors',
  'inactive_30',
  'inactive_60',
  'inactive_90',
  'inactive_90_plus',
];

/** Clave i18n del label de cada preset. */
const PRESET_LABEL_KEY: Record<RecipientPreset, string> = {
  all_active: 'marketingEmail.segment.allActive',
  debtors: 'marketingEmail.segment.debtors',
  inactive_30: 'marketingEmail.segment.inactive30',
  inactive_60: 'marketingEmail.segment.inactive60',
  inactive_90: 'marketingEmail.segment.inactive90',
  inactive_90_plus: 'marketingEmail.segment.inactive120',
  manual: 'marketingEmail.segment.manualSelection',
  panel_assistants: 'marketingEmail.segment.panelAssistants',
};

const TEMPLATE_VARIABLES: Array<{ token: string; label: string }> = [
  { token: '{{nombre}}', label: 'Nombre paciente' },
  { token: '{{apellido}}', label: 'Apellido paciente' },
  { token: '{{profesional}}', label: 'Profesional' },
  { token: '{{consultorio_nombre}}', label: 'Nombre consultorio' },
  { token: '{{consultorio_direccion}}', label: 'Direccion consultorio' },
  { token: '{{consultorio_telefono}}', label: 'Telefono consultorio' },
  { token: '{{fecha}}', label: 'Fecha' },
  { token: '{{hora}}', label: 'Hora' },
  { token: '{{patient_name}}', label: 'Patient name' },
  { token: '{{clinic_name}}', label: 'Clinic name' },
  { token: '{{last_visit_date}}', label: 'Last visit date' },
  { token: '{{pending_balance}}', label: 'Pending balance' },
  { token: '{{booking_url}}', label: 'Booking URL' },
];

function hasMeaningfulHtmlContent(html: string): boolean {
  const withoutTags = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return withoutTags.length > 0;
}

function applyTemplateValues(text: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((acc, [token, value]) => acc.split(token).join(value), text);
}

function buildEmailFromPrompt(prompt: string): AiSuggestion {
  const lower = prompt.toLowerCase();
  if (lower.includes('promo') || lower.includes('descuento') || lower.includes('oferta')) {
    return {
      replyTo: '',
      subject: 'Oferta especial para pacientes activos',
      preheader: 'Promo por tiempo limitado para tu proximo plan.',
      body:
        '<p>Hola {{nombre}},</p><p>Tenemos una promo especial para vos esta semana.</p><ul><li>10% de descuento en planes</li><li>Evaluacion postural sin costo</li></ul><p>Reserva tu lugar respondiendo este mail.</p>',
    };
  }
  if (lower.includes('recordatorio') || lower.includes('turno') || lower.includes('cita')) {
    return {
      replyTo: '',
      subject: 'Recordatorio de turno {{fecha}} {{hora}}',
      preheader: 'Tu turno esta confirmado.',
      body:
        '<p>Hola {{nombre}},</p><p>Te recordamos tu turno:<br>Fecha: {{fecha}}<br>Hora: {{hora}}<br>Profesional: {{profesional}}</p><p>Si necesitas reprogramar, responde este correo.</p>',
    };
  }
  return {
    replyTo: '',
    subject: 'Mensaje para pacientes',
    preheader: prompt.slice(0, 80),
    body: `<p>Hola {{nombre}},</p><p>${prompt}</p><p>Saludos,<br>Equipo de la clinica</p>`,
  };
}

const inputCls =
  'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 transition';

/** Destinatarios al momento del envío (engagement no disponible en el panel). */
function campaignDeliveredCount(c: Campaign): number {
  return c.status === 'sent' ? c.recipientCount : 0;
}

const STATUS_BADGE: Record<CampaignStatus, string> = {
  sent: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800',
  scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800',
  ready: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800',
  draft: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  failed: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
      {label}
      {required ? ' *' : ''}
    </label>
    {children}
  </div>
);

// ── Modal: distribución (segmentos + selección manual) ───────────────────────

const DistributionModal: React.FC<{
  token: string;
  clinicId: string;
  selectedIds: number[];
  selectedPresets: RecipientPreset[];
  manualEnabled: boolean;
  onApply: (ids: number[], presets: RecipientPreset[], manualEnabled: boolean) => void;
  onClose: () => void;
}> = ({ token, clinicId, selectedIds, selectedPresets, manualEnabled, onApply, onClose }) => {
  const { t } = useTranslation();
  const [selectedSegments, setSelectedSegments] = useState<Set<RecipientPreset>>(new Set(selectedPresets));
  const [manualMode, setManualMode] = useState<boolean>(manualEnabled);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [local, setLocal] = useState<Set<number>>(new Set(selectedIds));
  const [patients, setPatients] = useState<Recipient[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);

  const selectedSegmentList = useMemo(() => Array.from(selectedSegments), [selectedSegments]);

  // Vista previa del total (debounce) al cambiar segmentos/selección manual.
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const presets = selectedSegmentList;
      const manualIds = manualMode ? Array.from(local) : [];
      if (presets.length === 0 && manualIds.length === 0) {
        if (!cancelled) setPreviewCount(0);
        return;
      }
      try {
        const preview = await previewAudience(token, { clinicId, segmentPresets: presets, manualPatientIds: manualIds, sampleSize: 1 });
        if (!cancelled) setPreviewCount(preview.total);
      } catch {
        if (!cancelled) setPreviewCount(0);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [selectedSegmentList, manualMode, local, token, clinicId]);

  // Carga de pacientes para la selección manual (debounce sobre la búsqueda).
  useEffect(() => {
    if (!manualMode) return;
    let cancelled = false;
    setPatientsLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const rows = await searchPatients(token, { clinicId, q: search, onlyActive });
        if (!cancelled) {
          setPatients(rows.map((p: CampaignPatient) => ({ id: p.pacienteId, name: `${p.nombre} ${p.apellido}`.trim(), email: p.email })));
        }
      } catch {
        if (!cancelled) setPatients([]);
      } finally {
        if (!cancelled) setPatientsLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [manualMode, search, onlyActive, token, clinicId]);

  const toggle = (id: number) => {
    setLocal((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const apply = () => {
    onApply(Array.from(local), selectedSegmentList, manualMode);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="w-full max-w-2xl max-h-[82vh] overflow-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-bold text-gray-900 dark:text-white">{t('marketingEmail.distribution.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">{t('common.close')}</button>
        </div>

        <div className="p-5 space-y-4">
          <Field label={t('marketingEmail.distribution.deliverySegment')} required>
            <div className="space-y-1.5">
              {SEGMENT_PRESETS.map((preset) => {
                const checked = selectedSegments.has(preset);
                return (
                  <label key={preset} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setSelectedSegments((prev) => {
                          const next = new Set(prev);
                          if (next.has(preset)) next.delete(preset);
                          else next.add(preset);
                          return next;
                        })
                      }
                      className="accent-teal-600"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">{t(PRESET_LABEL_KEY[preset])}</span>
                  </label>
                );
              })}
              <label className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-t border-gray-100 dark:border-gray-700 pt-2">
                <input type="checkbox" checked={manualMode} onChange={() => setManualMode((v) => !v)} className="accent-teal-600" />
                <span className="text-sm text-gray-900 dark:text-white">{t('marketingEmail.segment.manualSelection')}</span>
              </label>

              {/* Equipo interno: no son pacientes, va separado para que no se confunda. */}
              <label className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-t border-gray-100 dark:border-gray-700 pt-2">
                <input
                  type="checkbox"
                  checked={selectedSegments.has('panel_assistants')}
                  onChange={() =>
                    setSelectedSegments((prev) => {
                      const next = new Set(prev);
                      if (next.has('panel_assistants')) next.delete('panel_assistants');
                      else next.add('panel_assistants');
                      return next;
                    })
                  }
                  className="accent-teal-600 mt-0.5"
                />
                <span className="text-sm">
                  <span className="text-gray-900 dark:text-white">{t('marketingEmail.segment.panelAssistants')}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {t('marketingEmail.segment.panelAssistantsHint')}
                  </span>
                </span>
              </label>
            </div>
          </Field>

          {selectedSegmentList.length > 0 || (manualMode && local.size > 0) ? (
            <div className="rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 px-3 py-2 text-sm text-teal-700 dark:text-teal-300">
              {t('marketingEmail.distribution.totalUniqueBySegments')}: <strong>{previewCount}</strong>
            </div>
          ) : null}

          {manualMode ? (
            <>
              <div className="flex gap-2">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('marketingEmail.distribution.searchPatient')} className={inputCls} />
                <button
                  onClick={() => setOnlyActive((v) => !v)}
                  className={`rounded-lg border px-3 text-xs ${onlyActive ? 'border-teal-500 text-teal-700 dark:text-teal-300' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}
                >
                  {t('marketingEmail.distribution.onlyActive')}
                </button>
              </div>
              <div className="space-y-1 max-h-64 overflow-auto">
                {patientsLoading ? (
                  <LoadingBlock size="sm" minHeightClassName="min-h-[3rem]" />
                ) : patients.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 px-3 py-2">{t('marketingEmail.distribution.noPatients')}</p>
                ) : (
                  patients.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                      <input type="checkbox" checked={local.has(p.id)} onChange={() => toggle(p.id)} className="accent-teal-600" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white truncate">{p.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.email}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300">{t('common.cancel')}</button>
          <button
            onClick={apply}
            disabled={selectedSegments.size === 0 && (!manualMode || local.size === 0)}
            className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40"
          >
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal: asistente de contenido (plantillas + generador local) ─────────────

const AiModal: React.FC<{ onApply: (s: AiSuggestion) => void; onClose: () => void }> = ({ onApply, onClose }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');

  const templates: Array<{ label: string; suggestion: AiSuggestion }> = [
    {
      label: t('marketingEmail.aiModal.tplPromo'),
      suggestion: {
        replyTo: '',
        subject: 'Novedades de {{clinic_name}} para vos',
        preheader: 'Beneficios por tiempo limitado.',
        body: '<p>Hola {{patient_name}} 👋</p><p>En {{clinic_name}} queremos contarte nuestras novedades y beneficios.</p><p><a href="{{booking_url}}">Reservar turno</a></p><p>Te esperamos 💚</p>',
      },
    },
    {
      label: t('marketingEmail.aiModal.tplInactive'),
      suggestion: {
        replyTo: '',
        subject: '{{patient_name}}, te esperamos en {{clinic_name}}',
        preheader: 'Paso un tiempo desde tu ultima visita.',
        body: '<p>Hola {{patient_name}} 👋</p><p>Notamos que paso un tiempo desde tu ultima visita ({{last_visit_date}}).</p><p>Tu bienestar sigue siendo nuestra prioridad 💚</p><p><a href="{{booking_url}}">Solicitar turno</a></p>',
      },
    },
    {
      label: t('marketingEmail.aiModal.tplDebtors'),
      suggestion: {
        replyTo: '',
        subject: 'Informacion de saldo pendiente en {{clinic_name}}',
        preheader: 'Estado de tu cuenta.',
        body: '<p>Hola {{patient_name}} 👋</p><p>Registramos un saldo pendiente en tu cuenta: <strong>{{pending_balance}}</strong>.</p><p>Si ya lo abonaste, desestima este mensaje.</p>',
      },
    },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="w-full max-w-xl rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-bold text-gray-900 dark:text-white">{t('marketingEmail.composer.aiAssistant')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">{t('common.close')}</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('marketingEmail.aiModal.promoTemplates')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {templates.map((template) => (
              <button
                key={template.label}
                type="button"
                onClick={() => {
                  onApply(template.suggestion);
                  onClose();
                }}
                className="rounded-md border border-teal-200 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 px-2.5 py-2 text-xs text-teal-700 dark:text-teal-300 text-left"
              >
                {template.label}
              </button>
            ))}
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder={t('marketingEmail.aiModal.promptPlaceholder')}
            className={`${inputCls} resize-none`}
          />
          <button
            onClick={() => {
              if (!prompt.trim()) return;
              onApply(buildEmailFromPrompt(prompt));
              onClose();
            }}
            disabled={!prompt.trim()}
            className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 text-white py-2 font-semibold disabled:opacity-50"
          >
            {t('marketingEmail.aiModal.generateMail')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal: vista previa del email ────────────────────────────────────────────

const PreviewModal: React.FC<{
  draft: EmailDraft;
  recipientText: string;
  clinicName: string;
  onClose: () => void;
}> = ({ draft, recipientText, clinicName, onClose }) => {
  const { t } = useTranslation();
  const [useSampleData, setUseSampleData] = useState(true);

  const sampleValues: Record<string, string> = {
    '{{nombre}}': 'Juan',
    '{{apellido}}': 'Perez',
    '{{patient_name}}': 'Juan Perez',
    '{{profesional}}': 'Profesional asignado',
    '{{professional_name}}': 'Profesional asignado',
    '{{clinic_name}}': clinicName,
    '{{consultorio_nombre}}': clinicName,
    '{{fecha}}': new Date().toLocaleDateString('es-AR'),
    '{{hora}}': '10:00',
    '{{last_visit_date}}': new Date(Date.now() - 60 * 864e5).toLocaleDateString('es-AR'),
    '{{pending_balance}}': '$ 15.000',
    '{{booking_url}}': '#',
  };
  const shown = useSampleData
    ? {
        subject: applyTemplateValues(draft.subject, sampleValues),
        preheader: applyTemplateValues(draft.preheader, sampleValues),
        body: applyTemplateValues(draft.body, sampleValues),
      }
    : draft;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-bold text-gray-900 dark:text-white">{t('marketingEmail.preview.title')}</h2>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button type="button" onClick={() => setUseSampleData(false)} className={`px-3 py-1.5 text-xs ${!useSampleData ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
                {t('marketingEmail.preview.original')}
              </button>
              <button type="button" onClick={() => setUseSampleData(true)} className={`px-3 py-1.5 text-xs ${useSampleData ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
                {t('marketingEmail.preview.withData')}
              </button>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">{t('common.close')}</button>
          </div>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3 text-sm space-y-1">
            <p className="text-gray-700 dark:text-gray-200"><strong>{t('marketingEmail.preview.to')}:</strong> {recipientText}</p>
            <p className="text-gray-700 dark:text-gray-200"><strong>{t('marketingEmail.preview.subject')}:</strong> {shown.subject.trim() || t('marketingEmail.preview.noSubject')}</p>
            <p className="text-gray-700 dark:text-gray-200"><strong>{t('marketingEmail.preview.preheader')}:</strong> {shown.preheader.trim() || t('marketingEmail.preview.noPreheader')}</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
            <div className="text-[15px] leading-relaxed text-gray-900 dark:text-gray-100" dangerouslySetInnerHTML={{ __html: shown.body || `<p>${t('marketingEmail.preview.noContent')}</p>` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Modal: detalle de campaña ────────────────────────────────────────────────

const CampaignDetailModal: React.FC<{ campaign: Campaign; clinicName: string; onClose: () => void }> = ({ campaign, clinicName, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'stats' | 'content'>('stats');
  const delivered = campaignDeliveredCount(campaign);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 dark:text-white truncate">{campaign.name}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{clinicName} · {campaign.subject || t('marketingEmail.preview.noSubject')}</p>
          </div>
          <span className={`ml-auto inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[campaign.status]}`}>{t(`marketingEmail.status.${campaign.status}`)}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">{t('common.close')}</button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-1">
            <button type="button" onClick={() => setActiveTab('stats')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${activeTab === 'stats' ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300'}`}>
              {t('marketingEmail.detail.tabs.stats')}
            </button>
            <button type="button" onClick={() => setActiveTab('content')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${activeTab === 'content' ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300'}`}>
              {t('marketingEmail.detail.tabs.content')}
            </button>
          </div>

          {activeTab === 'stats' ? (
            <>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3 text-sm space-y-1">
                <p className="text-gray-700 dark:text-gray-200"><strong>{t('marketingEmail.table.audience')}:</strong> {campaign.audienceLabel || '—'}</p>
                <p className="text-gray-700 dark:text-gray-200"><strong>{t('marketingEmail.table.updated')}:</strong> {new Date(campaign.updatedAt).toLocaleString()}</p>
                {campaign.sentAt ? <p className="text-gray-700 dark:text-gray-200"><strong>{t('marketingEmail.preview.sentAt')}:</strong> {new Date(campaign.sentAt).toLocaleString()}</p> : null}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2.5 bg-white dark:bg-gray-900">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('marketingEmail.detail.recipients')}</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white">{delivered}</p>
                </div>
                <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-2.5 bg-white dark:bg-gray-900">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('marketingEmail.detail.engagement')}</p>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('marketingEmail.detail.engagementUnavailable')}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">{t('marketingEmail.detail.metricsNote')}</p>
            </>
          ) : (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
              <div className="text-[14px] leading-relaxed text-gray-900 dark:text-gray-100" dangerouslySetInnerHTML={{ __html: campaign.bodyHtml || `<p>${t('marketingEmail.preview.noContent')}</p>` }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────

export function CommunicationsPage() {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('communications.manage');
  const ownerClinics = useMemo(() => user?.scope.allowedTenantIds ?? [], [user]);
  const clinicNames = useClinicNames();
  const nameFor = useCallback((id: string) => clinicNames[id] ?? id, [clinicNames]);

  const [crmView, setCrmView] = useState<'list' | 'composer'>('list');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [detailCampaign, setDetailCampaign] = useState<Campaign | null>(null);

  // Estado del composer.
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);
  const [clinicId, setClinicId] = useState<string>(ownerClinics.length === 1 ? ownerClinics[0] : '');
  const [draft, setDraft] = useState<EmailDraft>(EMPTY_DRAFT);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [recipientPresets, setRecipientPresets] = useState<RecipientPreset[]>([]);
  const [manualEnabled, setManualEnabled] = useState(false);
  const [composerStatus, setComposerStatus] = useState<ComposerStatus>('draft');
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('send_now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [audienceCount, setAudienceCount] = useState(0);
  const [showDistModal, setShowDistModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showReplyTo, setShowReplyTo] = useState(false);
  const [showPreheader, setShowPreheader] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const editorHostRef = useRef<HTMLDivElement | null>(null);

  const hasSubject = draft.subject.trim().length > 0;
  const hasBody = hasMeaningfulHtmlContent(draft.body);
  const hasAudience = recipientPresets.length > 0 || (manualEnabled && selectedIds.length > 0);
  const canMarkReady = Boolean(clinicId) && hasSubject && hasBody && hasAudience;
  const canSendNow = composerStatus === 'ready' && !isSending;

  const toDraftIfReady = () => setComposerStatus((prev) => (prev === 'ready' ? 'draft' : prev));

  const recipientText = useMemo(() => {
    if (!hasAudience) return t('marketingEmail.composer.selectRecipients');
    const parts: string[] = [];
    if (recipientPresets.length > 0) parts.push(t('marketingEmail.recipients.segmentCount', { count: recipientPresets.length }));
    if (manualEnabled && selectedIds.length > 0) parts.push(t('marketingEmail.recipients.manualCount', { count: selectedIds.length }));
    return `${parts.join(' + ')} (${t('marketingEmail.recipients.uniqueCount', { count: audienceCount })})`;
  }, [hasAudience, recipientPresets, manualEnabled, selectedIds, audienceCount, t]);

  const loadCampaigns = useCallback(async () => {
    if (!token || !canManage) return;
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      setCampaigns(await listCampaigns(token));
    } catch (err) {
      setCampaignsError(err instanceof Error ? err.message : 'error');
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  }, [token, canManage]);

  useEffect(() => {
    if (crmView === 'list') void loadCampaigns();
  }, [crmView, loadCampaigns]);

  // Vista previa del total de audiencia en el composer.
  useEffect(() => {
    if (!token || !clinicId) {
      setAudienceCount(0);
      return;
    }
    const manualIds = manualEnabled ? selectedIds : [];
    if (recipientPresets.length === 0 && manualIds.length === 0) {
      setAudienceCount(0);
      return;
    }
    let cancelled = false;
    previewAudience(token, { clinicId, segmentPresets: recipientPresets, manualPatientIds: manualIds, sampleSize: 1 })
      .then((preview) => {
        if (!cancelled) setAudienceCount(preview.total);
      })
      .catch(() => {
        if (!cancelled) setAudienceCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [token, clinicId, recipientPresets, manualEnabled, selectedIds]);

  const clearComposer = () => {
    setDraft(EMPTY_DRAFT);
    setSelectedIds([]);
    setRecipientPresets([]);
    setManualEnabled(false);
    setComposerStatus('draft');
    setDeliveryMode('send_now');
    setScheduledAt('');
    setShowReplyTo(false);
    setShowPreheader(false);
    setClinicId(ownerClinics.length === 1 ? ownerClinics[0] : '');
  };

  const startNewCampaign = () => {
    clearComposer();
    setActiveCampaignId(null);
    setActionError(null);
    setCrmView('composer');
  };

  const openInComposer = (campaign: Campaign) => {
    setDraft({ replyTo: campaign.replyTo ?? '', subject: campaign.subject, preheader: campaign.preheader ?? '', body: campaign.bodyHtml });
    setClinicId(campaign.clinicId);
    setRecipientPresets(campaign.segmentPresets.filter((p) => p !== 'manual'));
    setManualEnabled(campaign.manualPatientIds.length > 0);
    setSelectedIds(campaign.manualPatientIds);
    setComposerStatus(campaign.status === 'sent' ? 'sent' : campaign.status === 'ready' || campaign.status === 'scheduled' ? 'ready' : 'draft');
    setDeliveryMode(campaign.scheduledAt ? 'schedule' : 'send_now');
    setScheduledAt(campaign.scheduledAt ? campaign.scheduledAt.slice(0, 16) : '');
    setActiveCampaignId(campaign.id);
    setActionError(null);
    setCrmView('composer');
  };

  const buildPayload = () => ({
    name: draft.subject.trim() ? draft.subject.trim().slice(0, 48) : t('marketingEmail.actions.newCampaignFallback'),
    subject: draft.subject.trim(),
    preheader: draft.preheader.trim() || null,
    bodyHtml: draft.body,
    replyTo: draft.replyTo.trim() || null,
    segmentPresets: recipientPresets,
    manualPatientIds: manualEnabled ? selectedIds : [],
    audienceLabel: recipientText,
    scheduledAt: deliveryMode === 'schedule' && scheduledAt ? new Date(scheduledAt).toISOString() : null,
  });

  const persist = async (): Promise<Campaign | null> => {
    if (!token) return null;
    setActionError(null);
    setIsSaving(true);
    try {
      const payload = buildPayload();
      const saved = activeCampaignId
        ? await updateCampaign(token, activeCampaignId, payload)
        : await createCampaign(token, { clinicId, ...payload });
      setActiveCampaignId(saved.id);
      setCampaigns((prev) => {
        const exists = prev.some((c) => c.id === saved.id);
        return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev];
      });
      return saved;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'error');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const saveDraft = async () => {
    const saved = await persist();
    if (saved) setComposerStatus('draft');
  };

  const markReady = async () => {
    if (!canMarkReady) return;
    const saved = await persist();
    if (saved) setComposerStatus('ready');
  };

  const sendNow = async () => {
    if (!token || composerStatus !== 'ready' || isSending) return;
    setActionError(null);
    setIsSending(true);
    try {
      const saved = await persist();
      const id = saved?.id ?? activeCampaignId;
      if (!id) return;
      const result = await sendCampaign(token, id);
      setComposerStatus('sent');
      setCampaigns((prev) => {
        const exists = prev.some((c) => c.id === result.campaign.id);
        return exists ? prev.map((c) => (c.id === result.campaign.id ? result.campaign : c)) : [result.campaign, ...prev];
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'error');
    } finally {
      setIsSending(false);
    }
  };

  const openDetail = async (campaign: Campaign) => {
    setDetailCampaign(campaign);
    if (!token) return;
    try {
      setDetailCampaign(await getCampaign(token, campaign.id));
    } catch {
      // Best-effort: si falla el refresco, queda el de la lista.
    }
  };

  const primaryLabel =
    composerStatus === 'sent'
      ? t('marketingEmail.status.sent')
      : composerStatus === 'ready'
        ? t('marketingEmail.actions.send')
        : t('marketingEmail.actions.markReady');

  if (!canManage) {
    return (
      <div className="space-y-2 p-4 xl:p-6">
        <h1 className="text-2xl font-bold text-gray-950 dark:text-white">{t('marketingEmail.forbidden.title')}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('marketingEmail.forbidden.message')}</p>
      </div>
    );
  }

  // ── Vista LISTA ──
  if (crmView === 'list') {
    const totalSent = campaigns.reduce((acc, c) => acc + campaignDeliveredCount(c), 0);
    return (
      <div className="space-y-3 p-4 xl:p-6">
        {detailCampaign ? (
          <CampaignDetailModal campaign={detailCampaign} clinicName={nameFor(detailCampaign.clinicId)} onClose={() => setDetailCampaign(null)} />
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-950 dark:text-white">{t('marketingEmail.title')}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('marketingEmail.description')}</p>
          </div>
          <button onClick={startNewCampaign} className="ml-auto rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2">
            {t('marketingEmail.actions.newMail')}
          </button>
        </div>

        {campaignsError ? (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">{campaignsError}</div>
        ) : null}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('marketingEmail.metrics.campaigns')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{campaigns.length}</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('marketingEmail.metrics.sentEmails')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalSent}</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('marketingEmail.metrics.sentCampaigns')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{campaigns.filter((c) => c.status === 'sent').length}</p>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('marketingEmail.table.title')}</h2>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="text-left font-medium px-4 py-2">{t('marketingEmail.table.campaign')}</th>
                  <th className="text-left font-medium px-4 py-2">{t('marketingEmail.table.clinic')}</th>
                  <th className="text-left font-medium px-4 py-2">{t('marketingEmail.table.status')}</th>
                  <th className="text-left font-medium px-4 py-2">{t('marketingEmail.table.audience')}</th>
                  <th className="text-left font-medium px-4 py-2">{t('marketingEmail.table.sentEmails')}</th>
                  <th className="text-left font-medium px-4 py-2">{t('marketingEmail.table.updated')}</th>
                  <th className="text-right font-medium px-4 py-2">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {campaignsLoading ? (
                  <tr><td colSpan={7}><LoadingBlock minHeightClassName="min-h-[8rem]" /></td></tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-0 py-0">
                      <EmptyState
                        title={t('marketingEmail.table.emptyTitle')}
                        message={t('marketingEmail.table.empty')}
                        action={
                          <button
                            type="button"
                            onClick={() => setCrmView('composer')}
                            className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700"
                          >
                            {t('marketingEmail.actions.newMail')}
                          </button>
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-t border-gray-100 dark:border-gray-700/60">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[320px]">{campaign.subject || t('marketingEmail.preview.noSubject')}</p>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">{nameFor(campaign.clinicId)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[campaign.status]}`}>{t(`marketingEmail.status.${campaign.status}`)}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">{campaign.audienceLabel || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">{campaignDeliveredCount(campaign)}</td>
                      <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">{new Date(campaign.updatedAt).toLocaleString()}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openDetail(campaign)} className="rounded-md border border-gray-200 dark:border-gray-600 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-200">{t('common.view')}</button>
                          <button onClick={() => openInComposer(campaign)} className="rounded-md border border-gray-200 dark:border-gray-600 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-200">{t('common.edit')}</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ── Vista COMPOSER ──
  // Clínica fija al editar (inmutable en backend) o cuando el dueño tiene una sola.
  const readOnlyClinic = Boolean(activeCampaignId) || ownerClinics.length === 1;
  return (
    <div className="flex flex-col h-full min-h-0">
      {showDistModal && clinicId ? (
        <DistributionModal
          token={token ?? ''}
          clinicId={clinicId}
          selectedIds={selectedIds}
          selectedPresets={recipientPresets}
          manualEnabled={manualEnabled}
          onApply={(ids, presets, manual) => {
            setSelectedIds(ids);
            setRecipientPresets(presets);
            setManualEnabled(manual);
            toDraftIfReady();
          }}
          onClose={() => setShowDistModal(false)}
        />
      ) : null}
      {showAiModal ? <AiModal onApply={(s) => { setDraft((d) => ({ ...d, ...s })); toDraftIfReady(); }} onClose={() => setShowAiModal(false)} /> : null}
      {showPreviewModal ? <PreviewModal draft={draft} recipientText={recipientText} clinicName={nameFor(clinicId)} onClose={() => setShowPreviewModal(false)} /> : null}

      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 space-y-2">
        {actionError ? <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">{actionError}</div> : null}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setCrmView('list')} className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2 text-gray-700 dark:text-gray-200">{t('common.back')}</button>
          <div className="flex-1 flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => (composerStatus === 'ready' ? void sendNow() : void markReady())} disabled={(composerStatus === 'draft' ? !canMarkReady || isSaving : !canSendNow) || composerStatus === 'sent'} className="rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-40">
              {primaryLabel}
            </button>
            <button onClick={() => void saveDraft()} disabled={isSaving} className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2 text-gray-700 dark:text-gray-200 disabled:opacity-50">{t('marketingEmail.actions.saveDraft')}</button>
            <button onClick={() => setShowAiModal(true)} className="rounded-md border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 text-sm px-3 py-2">{t('marketingEmail.composer.aiAssistant')}</button>
            <button onClick={() => setShowPreviewModal(true)} className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2 text-gray-700 dark:text-gray-200">{t('marketingEmail.composer.preview')}</button>
            <select value={deliveryMode} onChange={(e) => { setDeliveryMode(e.target.value as DeliveryMode); toDraftIfReady(); }} className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2 text-gray-700 dark:text-gray-200">
              <option value="send_now">{t('marketingEmail.composer.sendNowOption')}</option>
              <option value="schedule">{t('marketingEmail.composer.scheduleOption')}</option>
            </select>
            {deliveryMode === 'schedule' ? (
              <input type="datetime-local" value={scheduledAt} onChange={(e) => { setScheduledAt(e.target.value); toDraftIfReady(); }} className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2 text-gray-700 dark:text-gray-200" />
            ) : null}
          </div>
          <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE[composerStatus]}`}>{t(`marketingEmail.status.${composerStatus}`)}</span>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-900 p-2 md:p-3">
        <section className="h-full min-h-0 flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 space-y-2">
            {/* Clínica destino */}
            <div className="flex items-center gap-3">
              <label className="w-20 text-sm text-gray-500 dark:text-gray-400">{t('marketingEmail.composer.clinic')}</label>
              {readOnlyClinic ? (
                <span className="text-sm font-medium text-gray-900 dark:text-white">{nameFor(clinicId)}</span>
              ) : (
                <select value={clinicId} onChange={(e) => { setClinicId(e.target.value); setSelectedIds([]); setRecipientPresets([]); setManualEnabled(false); toDraftIfReady(); }} className={inputCls} aria-label={t('marketingEmail.composer.clinic')}>
                  <option value="">{t('marketingEmail.composer.selectClinic')}</option>
                  {ownerClinics.map((id) => (
                    <option key={id} value={id}>{nameFor(id)}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Destinatarios */}
            <div className="flex items-start gap-3">
              <label className="w-20 pt-2 text-sm text-gray-500 dark:text-gray-400">{t('marketingEmail.composer.to')}</label>
              <div className="flex-1 space-y-1.5">
                <button onClick={() => setShowDistModal(true)} disabled={!clinicId} className={`${inputCls} text-left disabled:opacity-50 ${hasAudience ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20' : ''}`}>
                  {recipientText}
                </button>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {!showReplyTo ? <button onClick={() => setShowReplyTo(true)} className="text-xs font-medium text-teal-600 dark:text-teal-300">{t('marketingEmail.composer.replyTo')}</button> : null}
                  {!showPreheader ? <button onClick={() => setShowPreheader(true)} className="text-xs text-teal-600 dark:text-teal-300">{t('marketingEmail.composer.preheader')}</button> : null}
                </div>
              </div>
            </div>

            {showReplyTo ? (
              <div className="flex items-center gap-3">
                <label className="w-20 text-sm text-gray-500 dark:text-gray-400">{t('marketingEmail.composer.replyTo')}</label>
                <input value={draft.replyTo} onChange={(e) => { setDraft((d) => ({ ...d, replyTo: e.target.value })); toDraftIfReady(); }} placeholder={t('marketingEmail.composer.replyToPlaceholder')} className={inputCls} />
              </div>
            ) : null}

            <div className="flex items-center gap-3">
              <label className="w-20 text-sm text-gray-500 dark:text-gray-400">{t('marketingEmail.composer.subject')}</label>
              <input value={draft.subject} onChange={(e) => { setDraft((d) => ({ ...d, subject: e.target.value })); toDraftIfReady(); }} placeholder={t('marketingEmail.composer.subject')} className={inputCls} />
            </div>

            {showPreheader ? (
              <div className="flex items-center gap-3">
                <label className="w-20 text-sm text-gray-500 dark:text-gray-400">{t('marketingEmail.composer.preheader')}</label>
                <input value={draft.preheader} onChange={(e) => { setDraft((d) => ({ ...d, preheader: e.target.value })); toDraftIfReady(); }} placeholder={t('marketingEmail.composer.preheaderPlaceholder')} className={inputCls} />
              </div>
            ) : null}
          </div>

          <div ref={editorHostRef} className="flex-1 min-h-0 p-2 md:p-3">
            <Editor
              tinymceScriptSrc="/tinymce/tinymce.min.js"
              licenseKey="gpl"
              value={draft.body}
              onEditorChange={(content) => { setDraft((prev) => ({ ...prev, body: content })); toDraftIfReady(); }}
              init={{
                height: 420,
                menubar: false,
                branding: false,
                statusbar: true,
                placeholder: t('marketingEmail.composer.editorPlaceholder'),
                plugins: 'link lists',
                setup: (editor) => {
                  editor.ui.registry.addMenuButton('variablesMenu', {
                    text: t('marketingEmail.composer.variables'),
                    fetch: (callback) => {
                      callback(
                        TEMPLATE_VARIABLES.map((variable) => ({
                          type: 'menuitem',
                          text: `${variable.label} (${variable.token})`,
                          onAction: () => {
                            editor.insertContent(variable.token);
                            toDraftIfReady();
                          },
                        }))
                      );
                    },
                  });
                },
                toolbar: 'blocks | bold italic underline | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link | variablesMenu | removeformat',
                content_style: 'body { font-family: Segoe UI, Calibri, sans-serif; font-size: 15px; line-height: 1.6; padding: 12px; }',
              }}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
